import {
  Directive,
  Input,
  OnInit,
  OnDestroy,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FeatureFlagService } from '../services/feature-flag.service';

/**
 * Structural directive to conditionally show/hide content based on feature flags
 *
 * @example
 * // Simple usage
 * <div *featureFlag="'NEW_DASHBOARD'">
 *   New Dashboard Content
 * </div>
 *
 * @example
 * // With else template
 * <div *featureFlag="'NEW_DASHBOARD'; else oldDashboard">
 *   New Dashboard
 * </div>
 * <ng-template #oldDashboard>
 *   Old Dashboard
 * </ng-template>
 */
@Directive({
  selector: '[featureFlag]',
  standalone: false,
})
export class FeatureFlagDirective implements OnInit, OnDestroy {
  @Input() featureFlag!: string;
  @Input() featureFlagElse?: TemplateRef<any>;

  private destroy$ = new Subject<void>();
  private hasView = false;

  constructor(
    private templateRef: TemplateRef<any>,
    private viewContainer: ViewContainerRef,
    private featureFlagService: FeatureFlagService
  ) {}

  ngOnInit() {
    if (!this.featureFlag) {
      console.warn('featureFlag directive requires a feature key');
      return;
    }

    this.featureFlagService
      .isEnabledAsync(this.featureFlag)
      .pipe(takeUntil(this.destroy$))
      .subscribe((enabled) => {
        this.updateView(enabled);
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateView(enabled: boolean): void {
    this.viewContainer.clear();
    this.hasView = false;

    if (enabled) {
      this.viewContainer.createEmbeddedView(this.templateRef);
      this.hasView = true;
    } else if (this.featureFlagElse) {
      this.viewContainer.createEmbeddedView(this.featureFlagElse);
      this.hasView = true;
    }
  }
}
